import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { ListMatchingBlockSettings } from './ListMatchingBlockSettings';
import { Col, Container, Row } from 'reactstrap';
import { R } from '@core/utils/r';
import { HTMLService } from '@business/services';
import { SortableElement, SortEnd } from 'react-sortable-hoc';
import { SortableList } from '@modules/common/Sortable';
import { Button } from '@core/components/Button';
import { useBloc } from '@core/utils/bloc';
import { ListMatchingBlockBloc } from './ListMatchingBlockBloc';
import { of } from 'rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import './index.scss';

export interface MatchingListItem {
  item: string;
  matchingItem: string;
}

function defineListMatchingBlockForm() {
  return formGroup({
    instructions: formControl<string>({
      initialValue: 'Instructions',
      label: 'Instructions',
      required: true,
    }),
    items: formArray(() => defineListMatchingItemsForm()),
    advance: defineBlockAdvanceForm(),
  });
}

function defineListMatchingItemsForm() {
  return formGroup({
    item: formControl<string>({
      initialValue: 'item',
      required: true,
    }),
    matchingItem: formControl<string>({
      initialValue: 'matching item',
      required: true,
    }),
  });
}

export type ListMatchingWidgetFormData = FormDataType<typeof defineListMatchingBlockForm>;
export type ListMatchingWidgetForm = ReturnType<typeof defineListMatchingBlockForm>;

export type ListMatchingItemForm = ReturnType<typeof defineListMatchingItemsForm>;

export enum ListMatchingBlockVariation {
  listMatching = 'listMatching',
}

export const ListMatchingBlock = memo((props: CourseBuilder.BlockProps<ListMatchingBlockVariation>) => {
  const { variation, preview, editable, lessonId } = props;
  const bloc = useBloc(ListMatchingBlockBloc, lessonId);
  const form = useMemo(defineListMatchingBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const listItems = useObservable(form.controls.items.value$, []);
  const [columnAItems, setColumnAItems] = useState<MatchingListItem[]>([]);
  const [columnBItems, setColumnBItems] = useState<MatchingListItem[]>([]);
  const [isMatchingCompleted, setIsMatchingCompleted] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState(false);

  useEffect(() => {
    setColumnAItems(R.shuffle(listItems));
    setColumnBItems(R.shuffle(listItems));
  }, [listItems]);

  useConfigureBlock({
    props,
    form,
    contentComponent: ListMatchingBlockSettings,
    contentProps: { form, variation },
  });

  const onFirstSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newColumnAOrder = R.move(columnAItems, oldIndex, newIndex);
      setColumnAItems(newColumnAOrder);
    },
    [columnAItems],
  );

  const onSecondSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newColumnBOrder = R.move(columnBItems, oldIndex, newIndex);
      setColumnBItems(newColumnBOrder);
    },
    [columnBItems],
  );

  const onResetMatching = useCallback(() => {
    setIsMatchingCompleted(false);
    setWrongAnswer(false);
    setColumnAItems(R.shuffle(listItems));
    setColumnBItems(R.shuffle(listItems));
  }, [isMatchingCompleted, wrongAnswer]);

  const onNext = useCallback(() => {
    let result = columnAItems.length === columnBItems.length && columnAItems.every((obj, index) => obj.item === columnBItems[index].item);
    if(!props.editable)
    {
      bloc.onProgress(lessonId || '', {
        title: form.controls.instructions.value,
        answer: [JSON.stringify(columnAItems) + JSON.stringify(columnBItems)],
        correct: JSON.stringify(listItems),
        status: result ? 'pass' : 'fail',
      })
      .subscribe();
    }
    if (result) {
      return setWrongAnswer(false), setIsMatchingCompleted(true)
    }
    else {
      return setWrongAnswer(true), setIsMatchingCompleted(false)
    }
  }, [wrongAnswer, isMatchingCompleted, columnAItems, columnBItems]);

  const onSubmit = useCallback(
    () => of(isMatchingCompleted && !wrongAnswer),
    [wrongAnswer, isMatchingCompleted],
  );

  useCourseForm(props.id, form, { onSubmit });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-list', 'mt-3', variation)} advanceForm={formValue.advance}>
        <div className='ml-4 mt-4'>
          <p>{HTMLService.toReact(form.controls.instructions.value || '')}</p>
          {!isMatchingCompleted && (
            <Row>
              <Col md={6}>
                <SortableList lockAxis="y" distance={2} onSortEnd={sort => onFirstSortEnd(sort)} useDragHandle={false} helperClass="z5000">
                  {columnAItems.map((obj, index) => {
                    return <SortableListMatchingFirstItem items={obj} index={index} i={index} />;
                  })}
                </SortableList>
              </Col>
              <Col md={6}>
                <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSecondSortEnd(sort)} useDragHandle={false} helperClass="z5000">
                  {columnBItems.map((obj, index) => {
                    return <SortableListMatchingSecondItem matchingItems={obj} index={index} i={index} />;
                  })}
                </SortableList>
              </Col>
              <div className="d-flex align-items-start mt-2 ml-3 flex-column">
                <Button type="button" onClick={onNext}>
                  Next
                </Button>
              </div>
            </Row>
          )}
          {isMatchingCompleted && (
            <div className="d-flex align-items-start mt-2 flex-column">
              <span className="text-success mb-2">Congratulations! You have completed this drill successfully.</span>
              <Button type="button" onClick={onResetMatching}>
                Redo Drill
              </Button>
            </div>
          )}
          {wrongAnswer && (
            <div className="d-flex align-items-start mt-2 flex-column">
              <span className="text-danger mb-2">You have to get 100% correct answers to pass this drill.</span>
              <Button type="button" onClick={onResetMatching}>
                Redo Drill
              </Button>
            </div>
          )}
        </div>
      </BlockContainer>
    </Container>
  );
});

interface ListItemFirstColumnProps {
  i: number;
  items: MatchingListItem;
}

const ListItemFirstColumn = memo((props: ListItemFirstColumnProps) => {
  const { items, i } = props;

  return (
    <Row key={`item-second-column-${i}`} className="list-matching-row">
      <Col md={12}>
        {items.item}
      </Col>
    </Row>
  );
});

interface ListItemSecondColumnProps {
  i: number;
  matchingItems: MatchingListItem;
}

const ListItemSecondColumn = memo((props: ListItemSecondColumnProps) => {
  const { matchingItems, i } = props;

  return (
    <Row key={`item-second-column-${i}`} className="list-matching-row">
      <Col md={12}>
        {matchingItems.matchingItem}
      </Col>
    </Row>
  );
});

export const SortableListMatchingFirstItem = SortableElement(ListItemFirstColumn);
export const SortableListMatchingSecondItem = SortableElement(ListItemSecondColumn);
